import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  getBlobData,
  getData,
  patchDataToken
} from "../axios/axiosHelper";
import { buildOperationListQuery } from "../features/installmentOperations/operationFilters";
import { validateReviewSubmission } from "../features/installmentOperations/operationPresentation";

const DEFAULT_FILTERS = {
  search: "",
  operationReference: "",
  operationStatus: "",
  contractReviewStatus: "",
  platformFeeStatus: "",
  datePreset: "all",
  dateFrom: "",
  dateTo: ""
};

const PAGE_SIZE = 10;
const EVENT_PAGE_SIZE = 10;

const REVIEW_CHECKLIST_DEFAULTS = {
  serialMatchesOperation: false,
  sameMerchant: false,
  sameCustomer: false,
  allPagesPresent: false,
  productDataClear: false,
  priceAndScheduleMatchSystem: false,
  platformFeeVisibleAsIndependentClause: false,
  merchantSignaturePresent: false,
  customerSignaturePresent: false,
  stampPresentIfRequired: false,
  scanQualityAcceptable: false,
  noUnacceptableEdits: false,
  acceptableForApproval: false
};

const MANUAL_FEE_DEFAULTS = {
  method: "",
  reference: "",
  note: "",
  paidAt: "",
  amount: "",
  currency: ""
};

const HOOK_TEXT = {
  en: {
    nav: "Installment Requests",
    loadFailed: "Could not load installment-operation data",
    summaryFailed: "The operation summary is unavailable; the list remains available.",
    contractUnavailable: "No contract is available for this operation",
    signedAttemptUnavailable: "No signed-contract upload attempt is available",
    reviewUnavailable: "No contract is available for review",
    reviewDecisionRequired: "Choose a review decision before saving",
    reviewChecklistRequired: "Complete the review checklist before approving the contract",
    rejectionReasonRequired: "A clear decision reason of at least 3 characters is required",
    confirmApproval:
      "Approve this contract? If the platform fee and all remaining gates are complete, the operation will activate automatically.",
    confirmReupload: "Require the owning merchant to upload a corrected signed contract?",
    confirmRejection:
      "Permanently reject this contract? Activation and re-upload will be disabled.",
    reviewUpdated: "Contract review updated",
    staleAttempt:
      "A newer signed-contract upload is available. Your text was kept; review the newest file before confirming again.",
    reviewConflict:
      "This contract review changed. Refresh and verify the latest state before trying again.",
    reviewFailed: "The contract review could not be saved safely.",
    feeContractUnavailable: "No contract is linked to the platform fee",
    paymentMethodRequired: "The payment method is required",
    paymentReferenceRequired: "The payment reference is required",
    confirmManualFee:
      "Confirm the platform fee manually? If contract approval and all remaining gates are complete, the operation will activate automatically.",
    feeConfirmed: "Platform fee confirmed"
  },
  ar: {
    nav: "طلبات التقسيط",
    loadFailed: "تعذر تحميل بيانات طلبات التقسيط",
    summaryFailed: "تعذر تحميل ملخص العمليات، ويمكن متابعة القائمة.",
    contractUnavailable: "لا يوجد عقد متاح لهذه العملية",
    signedAttemptUnavailable: "لا توجد محاولة رفع للعقد الموقع",
    reviewUnavailable: "لا يوجد عقد قابل للمراجعة",
    reviewDecisionRequired: "اختر قرار المراجعة قبل الحفظ",
    reviewChecklistRequired: "يجب استكمال قائمة المراجعة قبل اعتماد العقد",
    rejectionReasonRequired: "يجب إدخال سبب واضح للقرار لا يقل عن 3 أحرف",
    confirmApproval:
      "تأكيد اعتماد العقد؟ إذا كانت رسوم المنصة وباقي المتطلبات مكتملة فسيتم تفعيل العملية تلقائيًا.",
    confirmReupload: "طلب رفع عقد موقع مصحح من التاجر صاحب الطلب؟",
    confirmRejection:
      "رفض العقد نهائيًا؟ سيُمنع تفعيل الطلب وإعادة رفع العقد.",
    reviewUpdated: "تم تحديث مراجعة العقد",
    staleAttempt:
      "توجد نسخة موقعة أحدث. احتفظنا بالنص الذي كتبته؛ راجع أحدث ملف قبل تأكيد القرار مجددًا.",
    reviewConflict:
      "تغيرت مراجعة العقد. حدّث البيانات وتحقق من أحدث حالة قبل المحاولة مجددًا.",
    reviewFailed: "تعذر حفظ مراجعة العقد بصورة آمنة.",
    feeContractUnavailable: "لا يوجد عقد مرتبط برسوم المنصة",
    paymentMethodRequired: "طريقة السداد مطلوبة",
    paymentReferenceRequired: "مرجع السداد مطلوب",
    confirmManualFee:
      "تأكيد رسوم المنصة يدويًا؟ إذا كان العقد معتمدًا وباقي المتطلبات مكتملة فسيتم تفعيل العملية تلقائيًا.",
    feeConfirmed: "تم تأكيد رسوم المنصة"
  }
};

const getErrorMessage = (error, language) =>
  error?.response?.data?.message ||
  (HOOK_TEXT[language] || HOOK_TEXT.en).loadFailed;

const getApiErrorCode = (error) =>
  error?.response?.data?.code || error?.code || "";

const getReviewErrorMessage = (error, ui) => {
  const code = getApiErrorCode(error);
  if (code === "STALE_CONTRACT_UPLOAD_ATTEMPT") return ui.staleAttempt;
  if (
    [
      "CONTRACT_REVIEW_CONFLICT",
      "CONTRACT_NOT_REVIEWABLE",
      "CONTRACT_NOT_PENDING_REVIEW",
      "SIGNED_UPLOAD_ATTEMPT_NOT_REVIEWABLE"
    ].includes(code)
  ) {
    return ui.reviewConflict;
  }
  if (code === "CONTRACT_REVIEW_CHECKLIST_INCOMPLETE") {
    return ui.reviewChecklistRequired;
  }
  if (code === "CONTRACT_REVIEW_REASON_REQUIRED") {
    return ui.rejectionReasonRequired;
  }
  return ui.reviewFailed;
};

const AdminInstallmentOperationsHook = (language = "en") => {
  const ui = HOOK_TEXT[language] || HOOK_TEXT.en;

  const [operations, setOperations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({
    totalResults: 0,
    totalPages: 1,
    currentPage: 1,
    perPage: PAGE_SIZE
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(PAGE_SIZE);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchDraft, setSearchDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [summaryWarning, setSummaryWarning] = useState("");
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedFee, setSelectedFee] = useState(null);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsPagination, setEventsPagination] = useState({
    totalPages: 1,
    currentPage: 1,
    totalResults: 0,
    perPage: EVENT_PAGE_SIZE
  });
  const [eventPage, setEventPage] = useState(1);
  const [showDetails, setShowDetails] = useState(false);
  const [activeOperationId, setActiveOperationId] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [actionLoading, setActionLoading] = useState("");
  const [reviewChecklist, setReviewChecklist] = useState(REVIEW_CHECKLIST_DEFAULTS);
  const [reviewForm, setReviewForm] = useState({
    decision: "",
    decisionReason: "",
    notes: ""
  });
  const [reviewError, setReviewError] = useState("");
  const [reviewedAttemptId, setReviewedAttemptId] = useState("");
  const [manualFeeForm, setManualFeeForm] = useState(MANUAL_FEE_DEFAULTS);
  const operationListRequestRef = useRef(0);
  const operationDetailRequestRef = useRef(0);

  const getOperations = useCallback(() => {
    const requestId = operationListRequestRef.current + 1;
    operationListRequestRef.current = requestId;
    setLoading(true);
    setErrorMessage("");
    const query = buildOperationListQuery({ filters, page: currentPage, perPage });

    getData(`/admin/installment-operations?${query}`, true, language)
      .then((response) => {
        if (operationListRequestRef.current !== requestId) return;
        setOperations(response?.data?.data || []);
        setPagination(
          response?.data?.pagination || {
            totalResults: 0,
            totalPages: 1,
            currentPage,
            perPage
          }
        );
      })
      .catch((error) => {
        if (operationListRequestRef.current !== requestId) return;
        const message = getErrorMessage(error, language);
        setErrorMessage(message);
        toast.error(message);
      })
      .finally(() => {
        if (operationListRequestRef.current === requestId) setLoading(false);
      });
  }, [currentPage, filters, language, perPage]);

  const getSummary = useCallback(() => {
    setSummaryLoading(true);
    setSummaryWarning("");

    getData("/admin/installment-operations/summary", true, language)
      .then((response) => {
        setSummary(response?.data?.data || null);
      })
      .catch(() => {
        setSummary(null);
        setSummaryWarning(ui.summaryFailed);
      })
      .finally(() => setSummaryLoading(false));
  }, [language, ui.summaryFailed]);

  useEffect(() => {
    getOperations();
  }, [getOperations]);

  useEffect(() => {
    getSummary();
  }, [getSummary]);

  const updateFilter = (name, value) => {
    setFilters((previous) => ({
      ...previous,
      [name]: value
    }));
    setCurrentPage(1);
  };

  const updateFilters = (patch) => {
    setFilters((previous) => ({ ...previous, ...patch }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchDraft("");
    setCurrentPage(1);
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const search = searchDraft.trim();
      if (search === filters.search) return;
      setFilters((previous) => ({ ...previous, search }));
      setCurrentPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [filters.search, searchDraft]);

  const handlePerPageChange = (value) => {
    const nextValue = Number(value) || PAGE_SIZE;
    setPerPage(nextValue);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > (pagination.totalPages || 1)) return;
    setCurrentPage(pageNumber);
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  const refreshOperationDetails = useCallback((operationId) => {
    if (!operationId) return;
    const requestId = operationDetailRequestRef.current + 1;
    operationDetailRequestRef.current = requestId;
    setDetailLoading(true);

    const detailRequest = getData(
      `/admin/installment-operations/${operationId}`,
      true,
      language
    )
      .then((response) => {
        if (operationDetailRequestRef.current !== requestId) return;
        setSelectedOperation(response?.data?.data || null);
      })
      .catch((error) => {
        if (operationDetailRequestRef.current !== requestId) return;
        const message = getErrorMessage(error, language);
        toast.error(message);
        setShowDetails(false);
      })
      .finally(() => {
        if (operationDetailRequestRef.current === requestId) {
          setDetailLoading(false);
        }
      });

    const relatedRequests = Promise.allSettled([
      getData(`/admin/installment-operations/${operationId}/contract`, true, language),
      getData(`/admin/installment-operations/${operationId}/platform-fee`, true, language),
      getData(`/admin/installment-operations/${operationId}/dispute`, true, language)
    ])
      .then(([contractResult, feeResult, disputeResult]) => {
        if (operationDetailRequestRef.current !== requestId) return;
        setSelectedContract(
          contractResult.status === "fulfilled"
            ? contractResult.value?.data?.data || null
            : null
        );
        setSelectedFee(
          feeResult.status === "fulfilled" ? feeResult.value?.data?.data || null : null
        );
        setSelectedDispute(
          disputeResult.status === "fulfilled"
            ? disputeResult.value?.data?.data || null
            : null
        );
      });

    return Promise.allSettled([detailRequest, relatedRequests]);
  }, [language]);

  const getOperationEvents = useCallback((operationId, page = 1) => {
    if (!operationId) return;
    setEventsLoading(true);
    getData(
      `/admin/installment-operations/${operationId}/events?page=${page}&perPage=${EVENT_PAGE_SIZE}`,
      true,
      language
    )
      .then((response) => {
        setEvents(response?.data?.data || []);
        setEventsPagination(
          response?.data?.pagination || {
            totalPages: 1,
            currentPage: page,
            totalResults: 0,
            perPage: EVENT_PAGE_SIZE
          }
        );
      })
      .catch((error) => {
        toast.error(getErrorMessage(error, language));
      })
      .finally(() => setEventsLoading(false));
  }, [language]);

  useEffect(() => {
    if (showDetails && activeOperationId && activeTab === "events") {
      getOperationEvents(activeOperationId, eventPage);
    }
  }, [
    activeOperationId,
    activeTab,
    eventPage,
    getOperationEvents,
    showDetails
  ]);

  const openOperationDetails = (operationId) => {
    if (!operationId) return;
    setShowDetails(true);
    setActiveOperationId(operationId);
    setActiveTab("overview");
    setEventPage(1);
    setSelectedOperation(null);
    setSelectedContract(null);
    setSelectedFee(null);
    setSelectedDispute(null);
    setEvents([]);
    setReviewChecklist(REVIEW_CHECKLIST_DEFAULTS);
    setReviewForm({ decision: "", decisionReason: "", notes: "" });
    setReviewError("");
    setReviewedAttemptId("");
    setManualFeeForm(MANUAL_FEE_DEFAULTS);
    refreshOperationDetails(operationId);
  };

  const closeOperationDetails = () => {
    operationDetailRequestRef.current += 1;
    setShowDetails(false);
    setSelectedOperation(null);
    setSelectedContract(null);
    setSelectedFee(null);
    setSelectedDispute(null);
    setEvents([]);
    setActiveOperationId("");
    setReviewError("");
    setReviewedAttemptId("");
  };

  const refreshDetailWorkflow = () => {
    if (!activeOperationId) return;
    refreshOperationDetails(activeOperationId);
    if (activeTab === "events") {
      getOperationEvents(activeOperationId, eventPage);
    }
    getOperations();
    getSummary();
  };

  const handleEventPageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > (eventsPagination.totalPages || 1)) return;
    setEventPage(pageNumber);
  };

  const getEventPageNumbers = () => {
    const pages = [];
    const startPage = Math.max(1, eventPage - 1);
    const endPage = Math.min(eventsPagination.totalPages || 1, startPage + 2);
    for (let page = startPage; page <= endPage; page += 1) pages.push(page);
    return pages;
  };

  const contractFileApi = useMemo(
    () => ({
      fetchContractPdf: async ({ contractId, attemptId, type, signal }) => {
        const url =
          type === "generated"
            ? `/contracts/${contractId}/files/generated`
            : `/contracts/${contractId}/files/signed/${attemptId}`;
        const response = await getBlobData(url, true, language, { signal });
        return response.data;
      }
    }),
    [language]
  );

  const updateReviewChecklist = (field, value) => {
    setReviewChecklist((previous) => ({
      ...previous,
      [field]: value
    }));
  };

  const markReviewedAttempt = useCallback((attemptId) => {
    if (attemptId) setReviewedAttemptId(String(attemptId));
  }, []);

  const submitReview = async (submission = {}) => {
    const decision = submission.decision || reviewForm.decision;
    const contractId =
      selectedContract?.contract || selectedOperation?.generatedContract?.contract;
    const attemptId =
      selectedContract?.latestSignedUploadAttempt?.id ||
      selectedOperation?.signedUpload?.id;
    if (!contractId) return toast.error(ui.reviewUnavailable);
    if (!attemptId) return toast.error(ui.signedAttemptUnavailable);
    if (!["APPROVED", "REUPLOAD_REQUIRED", "REJECTED"].includes(decision)) {
      return toast.error(ui.reviewDecisionRequired);
    }
    const submittedAttemptId = submission.relatedUploadAttempt || attemptId;
    if (String(submittedAttemptId) !== String(attemptId)) {
      setReviewedAttemptId("");
      setReviewError(ui.staleAttempt);
      return toast.error(ui.staleAttempt);
    }
    const decisionReason =
      submission.decisionReason ?? reviewForm.decisionReason;
    const checklist = submission.checklist || reviewChecklist;
    const validation = validateReviewSubmission({
      decision,
      reason: decisionReason,
      checklist
    });
    if (!validation.valid) {
      return toast.error(
        decision === "APPROVED"
          ? ui.reviewChecklistRequired
          : decision
            ? ui.rejectionReasonRequired
            : ui.reviewDecisionRequired
      );
    }
    if (reviewedAttemptId !== String(attemptId)) {
      setReviewError(ui.staleAttempt);
      return toast.error(ui.staleAttempt);
    }

    const confirmation =
      decision === "APPROVED"
        ? ui.confirmApproval
        : decision === "REUPLOAD_REQUIRED"
          ? ui.confirmReupload
          : ui.confirmRejection;
    const confirmed = window.confirm(confirmation);
    if (!confirmed) return;

    setReviewError("");
    setActionLoading(`review-${decision}`);
    try {
      const response = await patchDataToken(
        `/contracts/${contractId}/review`,
        {
        decision,
        relatedUploadAttempt: attemptId,
        decisionReason:
          decision === "APPROVED" ? undefined : String(decisionReason).trim(),
        notes: submission.notes ?? reviewForm.notes,
        checklist
        },
        true,
        language
      );
      toast.success(response?.data?.message || ui.reviewUpdated);
      setReviewForm({ decision: "", decisionReason: "", notes: "" });
      setReviewedAttemptId("");
      refreshDetailWorkflow();
    } catch (error) {
      const code = getApiErrorCode(error);
      const message = getReviewErrorMessage(error, ui);
      if (code === "STALE_CONTRACT_UPLOAD_ATTEMPT") {
        setReviewedAttemptId("");
        await refreshOperationDetails(activeOperationId);
      } else if (error?.response?.status === 409) {
        await refreshOperationDetails(activeOperationId);
      }
      setReviewError(message);
      toast.error(message);
    } finally {
      setActionLoading("");
    }
  };

  const updateManualFeeForm = (field, value) => {
    setManualFeeForm((previous) => ({
      ...previous,
      [field]: value
    }));
  };

  const submitManualFeeConfirmation = (event) => {
    event.preventDefault();
    const contractId =
      selectedContract?.contract || selectedOperation?.generatedContract?.contract;
    if (!contractId) return toast.error(ui.feeContractUnavailable);
    if (!manualFeeForm.method.trim())
      return toast.error(ui.paymentMethodRequired);
    if (!manualFeeForm.reference.trim())
      return toast.error(ui.paymentReferenceRequired);
    const confirmed = window.confirm(ui.confirmManualFee);
    if (!confirmed) return;

    const payload = {
      method: manualFeeForm.method.trim(),
      reference: manualFeeForm.reference.trim(),
      note: manualFeeForm.note,
      paidAt: manualFeeForm.paidAt || undefined
    };
    if (manualFeeForm.amount !== "") payload.amount = Number(manualFeeForm.amount);
    if (manualFeeForm.currency.trim()) payload.currency = manualFeeForm.currency.trim();

    setActionLoading("manual-fee");
    patchDataToken(
      `/contracts/${contractId}/platform-fee/manual-confirmation`,
      payload,
      true,
      language
    )
      .then((response) => {
        toast.success(response?.data?.message || ui.feeConfirmed);
        setManualFeeForm(MANUAL_FEE_DEFAULTS);
        refreshDetailWorkflow();
      })
      .catch((error) => toast.error(getErrorMessage(error, language)))
      .finally(() => setActionLoading(""));
  };

  const derivedSummary = useMemo(() => {
    const countBy = (field, value) =>
      operations.filter((operation) => operation?.[field] === value).length;

    return {
      total: summary?.total ?? pagination.totalResults ?? operations.length,
      waitingReview:
        summary?.pendingReview ??
        countBy("contractReviewStatus", "PENDING_ADMIN_REVIEW"),
      reuploadRequired:
        summary?.reuploadRequired ??
        countBy("contractReviewStatus", "REUPLOAD_REQUIRED") +
          countBy("contractStatus", "REUPLOAD_REQUIRED"),
      contractApproved:
        summary?.contractApproved ?? countBy("contractStatus", "APPROVED"),
      waitingPlatformFee:
        summary?.feePending ?? countBy("platformFeeStatus", "PENDING_PAYMENT"),
      active:
        summary?.active ??
        countBy("operationStatus", "ACTIVE_INSTALLMENT_OPERATION"),
      inDispute: summary?.inDispute ?? countBy("disputeStatus", "IN_DISPUTE"),
      inconsistent:
        summary?.inconsistent ??
        operations.filter(
          (operation) => operation?.activationConsistency?.isConsistent === false
        ).length
    };
  }, [operations, pagination.totalResults, summary]);

  const refreshWorkspace = useCallback(() => {
    getOperations();
    getSummary();
  }, [getOperations, getSummary]);

  return {
    operations,
    summary: derivedSummary,
    summaryLoading,
    summaryWarning,
    loading,
    errorMessage,
    filters,
    searchDraft,
    setSearchDraft,
    pagination,
    currentPage,
    perPage,
    handlePageChange,
    handlePerPageChange,
    updateFilter,
    updateFilters,
    resetFilters,
    openOperationDetails,
    closeOperationDetails,
    selectedOperation,
    selectedContract,
    selectedFee,
    selectedDispute,
    events,
    eventsLoading,
    eventsPagination,
    eventPage,
    getEventPageNumbers,
    handleEventPageChange,
    showDetails,
    detailLoading,
    activeTab,
    setActiveTab,
    contractFileApi,
    actionLoading,
    reviewChecklist,
    reviewForm,
    reviewError,
    reviewedAttemptId,
    setReviewForm,
    updateReviewChecklist,
    markReviewedAttempt,
    submitReview,
    manualFeeForm,
    updateManualFeeForm,
    submitManualFeeConfirmation,
    refresh: refreshWorkspace
  };
};

export default AdminInstallmentOperationsHook;
